import React from 'react';
import { Home, ChevronRight, Volume2, Users, Heart, ArrowRight, ArrowLeft } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface WhatHappenedFirstGenSectionProps {
  onSelectPart?: (partId: string) => void;
}

export const WhatHappenedFirstGenSection: React.FC<WhatHappenedFirstGenSectionProps> = ({ onSelectPart }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          
          {/* Breadcrumb: The Dentons > Part 2 of 9: What happened to the first generation Dentons */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPart && onSelectPart('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Dentons</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Part 2 of 9: What happened to the first generation Dentons
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs"
            title="Listen to What happened to the first generation Dentons"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          WHAT HAPPENED TO THE FIRST GENERATION DENTONS
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Users className="w-3.5 h-3.5" />
          <span>Part 2 of the 9-Part Denton Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-10 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Section 1: The Pioneer Settler Father */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              The Pioneer Settler Father - John Frederick Denton's Short Life in New Zealand
            </h2>
          </div>

          <p>
            John Frederick’s family had been yeoman farmers (a class between the gentry and farm labourers) for several generations in the Great Barton area near Bury St Edmunds, Suffolk. His father, John Denton, first married Hannah, who died two years after their 1790 marriage. In 1792, aged 32, he married Elizabeth Newport (18) at St Nicholas’ Church, Rushbrooke. They had a family of eight boys, with John Frederick being the second son born 1796. John Frederick married Anne Maria Pryke on 28 July 1831, when he was 35 and Anne was 20. Anne was from the nearby parish of Little Whelnetham, where they married.
          </p>

          {/* Image 34: Little Whelnetham countryside */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/34 Little Whelnetham countryside.jpg"
              alt="Little Whelnetham countryside"
              caption="Little Whelnetham countryside, Suffolk"
              filenameHint="34 Little Whelnetham countryside.jpg"
            />
          </div>

          <p>
            John and Anne lived at Green Farm, a farm in the Rushbrooke Estate until 1845 when he took up role of steward for Fishley Farm, 20 km from Norwich after the death of its owner Colonel Robert Rushbrooke in 1845. John’s brother William was tenant farmer at another Rushbrooke property, Bridge Farm. A steward was a professional role for an educated respected man, managing the farm and tenant farmers, supervising agricultural workers, allocating and supervising duties, buying and selling on the owner’s behalf. He would have been provided with a private house on the estate.
          </p>

          <p>
            In 2021 Fishley Hall is operated as a luxury wedding venue and boutique hotel on 160 ha and including a large old barn and church. It had been completely restored and modernized.
          </p>

          <p>
            John and Anne had eight children in 13 years: Arthur John, William Henry, Frederick Robert, <em>Georgiana</em>, <em>Edward</em>, Elizabeth Anne (1842-45), Louisa and Thomas Charles. The italics indicated the children who were to die in 1851 in Christchurch.
          </p>

          <p>
            It must have been a late decision to go to Canterbury as the early selection criteria for emigrants required they be under 40 years old. When the Canterbury Association found they were about 300 people short a month before they were due to depart they relaxed the regulations, except for the religion and character conditions. John did have time to gather together some substantial farm equipment, obtain assisted passages for the two older sons, book two steerage passages for two of the boys, and a relatively large fore-cabin or himself and Anne, Georgiana, Louisa Alice and Thomas Charles. They had the means to travel as Colonists.
          </p>

          <p>
            During the 99-day voyage his son Frederick Robert was so ill he was not expected to live. One day he felt hungry and asked the matron of the sick bay for a mutton chop. She asked the ship’s doctor who said, in the hearing of Frederick’s brother Thomas, “yes, give him one if he wants it, for I don’t think he will be with us much longer.” It must have been a very good mutton chop because Frederick ate it and to the doctor’s surprise, he improved and regained his health from that point.
          </p>

          <p>
            The ladies on the ships dressed for their arrival in Port Lyttelton but many of them cried when they saw there were no shops but a straggle of cottages and barracks. Governor Sir George Grey and Lady Grey greeted the new arrivals and soothed over difficulties, such as the remission of all customs duties on the belongings the emigrants had brought with them.
          </p>

          <p>
            It took three weeks for the holds to be completely emptied. Some people stayed on board, some went to the barracks, others crossed the Port Hills to encampments in Hagley Park. Fever broke out in the camp settlement which caused the deaths of the three children and Anne.
          </p>

          <p>
            John Denton was granted about 23 ha on the banks of the Heathcote River, bounded by what became Opawa Road, Ford Road, Field Terrace and Ensors Road (Rural Section No 157). The paperwork was finally completed in 1858 but by then John had died so the title was issued in the names of Arthur and Frederick Denton, his two older sons. John also bought land in lower Lincoln Road, which he sold in April 1854.
          </p>

          <p>
            For a while the family lived in primitive conditions on the riverbank, but many others were in the same circumstances, until they built their sod cottages and small wooden houses. The boys were able to earn money by using the harness and carts they had brought out, with a bullock team and dray to carry goods, and going as far afield as the north side of the Waimakariri River. With no horses available they had bought bullocks from the Deans at Riccarton, which needed to be broken in and then trained to work as a team.
          </p>

          <p>
            Alice, only 5 when her mother died, was later taken in by Charles and Fanny Bridge, who farmed on an adjoining property. Little Thomas Charles was fostered out with a family for five years until he left aged only nine to make his way in the world – and he did.
          </p>

          <p>
            John wrote long reports home about the farming conditions and new settlement to his brothers. Who knows what he would have talked about to his sons as they looked at each other at the end of the day, and they must have keenly felt the absence of their dearly departed.
          </p>

          <p>
            John developed congestion of the chest and died 25 September 1856, aged 60. He is buried in the Barbadoes Street Cemetery.
          </p>

          {/* Image 35: John Denton Plot 33 */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/35 John Denton Plot 33 Barbadoes St Cemetery.jpg"
              alt="John Denton Plot 33 Barbadoes St Cemetery"
              caption="John Denton Plot 33 Barbadoes St Cemetery, Christchurch"
              filenameHint="35 John Denton Plot 33 Barbadoes St Cemetery.jpg"
            />
          </div>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 2: What Happened to the Four Denton Children */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              What Happened to the Four Denton Children Who Survived in Canterbury?
            </h2>
          </div>

          <p>
            The four Denton children who survived the sea journey and the typhoid that took the lives of their mother and three siblings, were Arthur, Frederick, Louisa and Charles. John Thomas Denton is descended from Frederick so we know more about his line of descent. The stories of his brothers come further on in this booklet.
          </p>

          <p>
            Arthur had a long and happy marriage to Julia, they had 11 children, and they mostly all ended up in the North Island. Thomas Charles also went north, and there is a mystery in his life with the total disappearance of his wife Helen, nee White shortly after the birth of their seventh child. One son, Ernest Harold, was killed in WW1 in Belgium. They lived mostly in the Manawatu area. His story also follows.
          </p>

          <p>
            Louisa, known as Alice, came to NZ as a five-year-old. She remembered arriving in Lyttelton, standing on the deck with her father and a brother when the Sir George Seymour entered the port on 17 December 1850. She said the hills were covered with tussock and two small houses on the flat looked like doll houses.
          </p>

          <p>
            In September 1851 after her mother died Alice was taken in by the Bridge family, who lived on the adjoining Opawa property, at Kaiapoi and Southbridge. Charles Joseph Bridge had come out on the Randolph and his diary of that journey is still available through the National Library.
          </p>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 3: Arthur John Denton */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              Arthur John Denton (10 February 1834 – 29 May 1923)
            </h2>
          </div>

          <p>
            When young Arthur Denton set sail on Sir George Seymour he took with him two books. His friends gave him a farewell gift, a book called RECIPES FOR HORSES with items such as “How to make your horse love you.” It was inscribed:
          </p>

          {/* Inscription Quote */}
          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-2 shadow-xs">
            <p className="italic">
              Receits for Horses as collected from his old friends and acquaintances before leaving England when he, A Denton was seventeen years old when living at Fishley Hall in the year of Our Lord 1850.
            </p>
          </blockquote>

          {/* Image 36: Inscription in book */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/36 Inscription in book Arthur_s friends gave him.jpg"
              alt="Inscription in book Arthur's friends gave him"
              caption="Inscription in book Arthur's friends gave him"
              filenameHint="36 Inscription in book Arthur_s friends gave him.jpg"
            />
          </div>

          <p>
            The other book was a volume that had been translated into several languages and had 84 editions, based on eyewitness accounts of British soldiers and Polish refugees fleeing Russian and Prussian occupied Poland in the 1790s.
          </p>

          <p>
            Arthur and William travelled on assisted passages and were listed as agricultural workers. Their younger brothers, Frederick (13) and Edward (11) were steerage passengers, at 15 Pounds each.
          </p>

          <p>
            It was Arthur who discovered a fire on board when the ship was off the Canary Islands, on 18 September 1851. He raised the alarm.
          </p>

          {/* Fire on Board Quote */}
          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-2 shadow-xs">
            <p className="italic">
              “We got the fire and all other buckets in the ship and in less than five minutes had formed a double line of the emigrants and were able to keep up a continuous supply of water to the seamen who alone were allowed to go below…The smoke by this time was pouring up the main hatchway pretty quickly and the women and children came pouring up from their quarters, most of them in a terrible state. After some half hour of such feelings as no one can imagine who has not stood as we did over a fire in the close proximity of several barrels of oil, spirits and gunpowder, it was declared extinguished….It appears that a man taking out rice for the children’s breakfasts had overturned his lantern and the straw between the casks became ignited….The Psalms for the day appeared as if composed for the occasion and were repeated at our daily Service in a most affecting manner.”
            </p>
          </blockquote>

          <p>
            This was written in “Journal of Voyage” by Cyrus Davie, who had missed his booked passage on the Randolph and so hitched a berth on Sir George Seymour. On 4 October he was transferred by boat to the Randolph (where all his luggage was) and he joined up with his cabin mate Charles Bridge. It would be Charles Bridge who would give Arthur’s young sister Alice a comfortable home after her mother died.
          </p>

          <p>
            Arthur laid claim to be being the first person to use a plough in Canterbury (and perhaps New Zealand). The plough was brought out on the ship, along with a wide range of brightly painted red and blue agricultural tools, and seed. He ploughed land and undertook harvesting on the Deans property at Riccarton.
          </p>

          {/* Images 37 & 37A: Deans farm & Sod cottage */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <ArchivalImageCard
              src="/images/37 Deans farm at Riccrton.jpg"
              alt="Deans farm at Riccarton"
              caption="Deans farm at Riccarton"
              filenameHint="37 Deans farm at Riccrton.jpg"
            />
            <ArchivalImageCard
              src="/images/37A Typical sod cottage in Christchurch, 1850_s.jpg"
              alt="Typical sod cottage in Christchurch, 1850s"
              caption="Typical sod cottage in Christchurch, 1850s"
              filenameHint="37A Typical sod cottage in Christchurch, 1850_s.jpg"
            />
          </div>

          {/* Subsection: Arthur's Story */}
          <div className="pt-4 space-y-4">
            <h3 className="font-serif text-xl md:text-2xl font-bold text-[#4a3525] border-b border-[#d8ceb9] pb-2">
              Arthur’s Story – from “A Family of Canterbury Settlers” by Adair Dunsford &amp; Russ Denton
            </h3>

            <p>
              Arthur was about 11 years of age when the family moved from Green Farm near Rushbrooke to Fishley Farm about 13 km WNW of Yarmouth on the Norfolk coast. He was 16 when he gained an assisted passage as an agricultural labourer on Sir George Seymour. The first years were marred with the typhoid deaths of his mother and three siblings within a year of arrival, and then his father’s illnesses. The surviving Dentons farmed at Opawa.
            </p>

            <p>
              After their father died in September 1856 the two elder sons, Arthur and Frederick, farmed together until May 1860 when they divided the land. Arthur kept 6.5 ha on the south side of Jacksons Creek and 4ha on Opawa Road. Both brothers sold in 1863 to Joshua Williams, later a Supreme Court judge of NZ.
            </p>

            <p>
              In March 1857 Arthur (23) married Julia Wilson (18), who had arrived with her parents on Sir George Pollock in January 1851, at St Michaels and All Angels Church, where many family members were married in the early years. They started their life together in a little sod cottage at Opawa.
            </p>

            {/* Image 38: St Michael's */}
            <div className="my-6">
              <ArchivalImageCard
                src="/images/38 St Michael_s.jpg"
                alt="St Michaels"
                caption="St Michael and All Angels Church, Christchurch"
                filenameHint="38 St Michael_s.jpg"
              />
            </div>

            <p>
              In 1864 Arthur bought land on the Rangiora – Leithfield Road which he kept for ten years, then moved to Oamaru. Arthur built a cottage of puddled clay and made the bricks himself. He worked briefly on the railway, then for 15 years as a ploughman with a team of horses. The rolling limestone hills of North Otago were cultivated for rich arable and sheep grazing land, and it was there that the frozen meat trade was developed on Totara Estate in 1882.
            </p>

            <p>
              About 1885 Arthur and Julia moved to northern Hawkes Bay where their eldest son Frederick Arthur was developing a property in the Nuhaka district, which he later sold to move to the broken hill country River Rock Station on the Nuhaka River. Arthur and Julia helped their son there before they retired to Gisborne.
            </p>

            <p>
              These photos below show the family clearing the bush in the Whaterata Forest to develop their farm. When Trevor Foss (1916 – 1996) returned from war service he got a rehab farm next door, at Morere, a 45-minute drive south of Gisborne. The area is now in commercial pine forest, all that hard work invisible.
            </p>

            <p className="font-serif italic text-sm text-[#5c4328] bg-[#f3ede0] p-4 rounded-xl border-l-2 border-[#7a5b3a]">
              Photos used courtesy of the Family Archive of Thema Adele Leach, nee Foss (granddaughter of Arthur and Julia Denton, through her mother Hilda, 1880 - 1943)
            </p>

            {/* Images 39, 40, 41: Bush clearing photos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
              <ArchivalImageCard
                src="/images/39 Artrhur_s family clearing bush near Gisborne.jpg"
                alt="Arthur's family clearing bush near Gisborne"
                caption="Arthur's family clearing bush near Gisborne"
                filenameHint="39 Artrhur_s family clearing bush near Gisborne.jpg"
              />
              <ArchivalImageCard
                src="/images/40 Bush clearing with Arthur Denton family members.jpg"
                alt="Bush clearing with Arthur Denton family members"
                caption="Bush clearing with Arthur Denton family members"
                filenameHint="40 Bush clearing with Arthur Denton family members.jpg"
              />
            </div>

            <div className="my-6">
              <ArchivalImageCard
                src="/images/41 Arthur Denton family captioned.jpg"
                alt="Arthur Denton family captioned"
                caption="Arthur Denton family captioned archival portrait"
                filenameHint="41 Arthur Denton family captioned.jpg"
              />
            </div>

            {/* Image 42: Arthur and Julia Denton */}
            <div className="space-y-2 my-6">
              <ArchivalImageCard
                src="/images/42 Arthur and Julia Denton.jpg"
                alt="Arthur and Julia Denton"
                caption="Arthur (1834 – 1923) and Julia (1839 – 1923) in retirement – they died 14 days apart"
                filenameHint="42 Arthur and Julia Denton.jpg"
              />
              <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
                Arthur (1834 – 1923) and Julia (1839 – 1923) in retirement – they died 14 days apart
              </p>
            </div>

            {/* Images 43 & 44: Formal photos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
              <ArchivalImageCard
                src="/images/43 Formal photo Arthur, Julie and daughter.jpg"
                alt="Formal photo Arthur, Julie and daughter"
                caption="Formal photo Arthur, Julia and daughter"
                filenameHint="43 Formal photo Arthur, Julie and daughter.jpg"
              />
              <ArchivalImageCard
                src="/images/44 Arthur Denton family portrait.jpg"
                alt="Arthur Denton family portrait"
                caption="Arthur Denton family portrait"
                filenameHint="44 Arthur Denton family portrait.jpg"
              />
            </div>
          </div>

          {/* Subsection: Inheritance from England */}
          <div className="pt-4 space-y-4">
            <h3 className="font-serif text-xl md:text-2xl font-bold text-[#4a3525] border-b border-[#d8ceb9] pb-2">
              Inheritance from England
            </h3>

            <p>
              In 1922 Arthur’s cousin Anna Sophia Denton died unmarried. Her father Thomas Charles had left his estate firstly to her and her mother, but on Anna’s death, to his brother John Frederick’s children in NZ. Her solicitor’s letter stated:
            </p>

            <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-2 shadow-xs">
              <p className="italic">
                “the real estate of T.C. Denton consisting of shops at Peckham and Bury St Edmunds has been realised and produced 7227 pounds. Great Eastern Railway debenture stock had an estimated worth of 954 Pounds.”
              </p>
            </blockquote>

            <p>
              Arthur received 25% of this estate.
            </p>
          </div>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 4: Thomas Charles Denton and Mystery of Helen Denton */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              Thomas Charles Denton – and the Mysterious Disappearance of Helen Denton in 1896
            </h2>
          </div>

          <p>
            Thomas Charles Denton, the youngest of the Dentons who came to NZ in 1850, orphaned at only nine years of age, married Helen White, the daughter of Oxford sawmiller George White in 1877. They lived on the Glentui farm Tom had bought in 1870 from his earnings gained from pig hunting, driving bullock and horse teams, and working on sheep stations (Mount Peel and Cheviot Hills). They sold that farm to buy 15 ha from Helen’s father at Powells Road, Oxford, and had their first three children there – Anne, Percival and Arthur.
          </p>

          {/* Image 45: Thomas Charles Denton on horseback */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/45 Thomas Charles Denton on horseback.jpg"
              alt="Thomas Charles Denton on horseback"
              caption="Tom Denton on horseback"
              filenameHint="45 Thomas Charles Denton on horseback.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Tom Denton
            </p>
          </div>

          <p>
            Tom was an excellent horseman and while at Oxford he had a very good mount called Young Barbarian who won several local races, with winning stakes from 5 – 25 guineas.
          </p>

          <p>
            In 1882 Tom and the Oxford Stationmaster George Billett successfully applied for bush-covered steep bluffs and flat land in a ballot on the south bank of the Oroua River, in the Manawatu area. While they cleared the land Tom worked at road building and splitting timber posts to cover costs. In 1889 the two men divided the farm in half and Tom got the lower part, with a house, stables and woolshed built from the totara they felled on the land.
          </p>

          <p>
            Tom and Helen had four more children (Ernest, Roy, Olive and Garry), who were mostly educated at Waiata School. Tom set up a road building business employing teams of horses and gangs of men to build the roads that were needed as the districts developed. He did contract ploughing and all types of agricultural contracting work, using the farm for grazing and milking a few cows to sell butter.
          </p>

          <p>
            Tom had a talent to work with horses and to treat them for all sorts of ailments. He was widely known, respected, hard-working and had the foundations for a happy life. Their youngest child was born in 1895, making their family complete – Annie Esther, Percival George, Arthur Charles, Ernest Harold, Roy, Olive May and baby Garry Edward.
          </p>

          <p>
            Life would have been very busy, but they were doing well in business, lived in a lovely area in a growing community. Tom played the violin at local country dances. People sought his advice on veterinary matters. The hardest work had been done.
          </p>

          <p>
            But in 1896 or thereabouts, as there is no actual date to be found, Helen disappeared. She never reappeared in their lives. If she had fallen in the river, her body would surely have been found. It would seem hard to believe Helen would have walked out on her young children. But one day she was there – the next she had gone, as if off the face of the earth.
          </p>

          <p>
            There is no evidence there was a police investigation, or that any suspicion fell on to Tom or any of their known acquaintances. Helen quite simply disappeared, aged 39 or 40, a relatively young woman with seven children, from a toddler to a 16-year-old.
          </p>

          <p>
            Annie, the eldest daughter, stepped into her mother’s shoes. She took over running the household and rearing the younger children. She stayed on the farm, in 1901 leasing it from her father in partnership with her brother Arthur, and they milked cows there for five years. The mystery was never solved. No one knows what happened to Helen Denton.
          </p>

          <p>
            Tom leased his home farm and lived on another farm further up the river. He employed a married couple to keep him company while he did off farm ploughing and contracting work. He got a throat infection in 1909 which gave him health issues and he died in 1910, aged 63, in Palmerston North hospital six weeks after admission with recurring throat problems.
          </p>

          <p>
            He left an estate of 2000 pounds, a considerable sum at that time. And he left behind the continued mystery of what happened to Helen.
          </p>

          {/* Image 46: Tom's family 1910 */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/46 Tom Denton_s family 1910.jpg"
              alt="Tom's family 1910"
              caption="Tom’s family taken after his death in 1910. Back L-R: Roy, Olive, Arthur & Ernest (who would be killed in WW1). Front L-R: Garry (known as Edward), Anne the surrogate mother & Percival."
              filenameHint="46 Tom Denton_s family 1910.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              Tom’s family taken after his death in 1910. Back L-R: Roy, Olive, Arthur &amp; Ernest (who would be killed in WW1). Front L-R: Garry (known as Edward), Anne the surrogate mother &amp; Percival.
            </p>
          </div>

          {/* Tom's Children Detail List */}
          <div className="space-y-6 pt-2">
            
            {/* ERNEST */}
            <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4">
              <p>
                <strong>ERNEST</strong> was farming at Rakauroa when he enlisted in NZ Rifle Brigade, 1st Battalion, as a rifleman, and was sent overseas with a unit of machine gunners, to fight in France and then killed in action at the battle of Bapaume, aged 34.
              </p>

              {/* Images 47 & 48: Battle of Bapaume & Death Certificate */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4">
                <ArchivalImageCard
                  src="/images/47 Second battle of Bapaume.jpg"
                  alt="Second battle of Bapaume"
                  caption="Second battle of Bapaume"
                  filenameHint="47 Second battle of Bapaume.jpg"
                />
                <ArchivalImageCard
                  src="/images/48 Death certificate Ernest Denton 1918.png"
                  alt="Death certificate Ernest Denton 1918"
                  caption="Death certificate Ernest Denton 1918"
                  filenameHint="48 Death certificate Ernest Denton 1918.png"
                />
              </div>

              <p>
                His will specified his estate (about 3000 Pounds) was to be divided equally between his sister Anne Jude and his brother Roy Denton.
              </p>
            </div>

            {/* ANNE */}
            <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-3">
              <p>
                <strong>ANNE (1878 – 1931)</strong> looked after the family until she married George Jude, an Australian working on the Fielding water supply, in 1904. In 1911 they moved to Victoria (Australia) but returned two years later to a dairy farm at Ihakara, and George worked for the Horowhenua County Council. They had five children - William George, Ralph (the family historian of his generation), Thomas Arthur, Martin Janes and Doreen Anne. After WW1 the Judes moved to Shannon where George worked on the Mangahao power scheme and other contracting jobs. Anne died in 1931 (aged 53), George died in 1948.
              </p>
            </div>

            {/* PERCY */}
            <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-3">
              <p>
                <strong>PERCY (1880 – 1952)</strong> had a primary education and helped on the family farm, and when he left school worked with his father on various contracting jobs. In 1910, aged 30, he married Kate Amelia Astridge and they had a daughter Elsie that year but Kate died soon after childbirth and the baby girl Elsie was raised Kate’s parents at Levin. Percy remarried in 1913, to Florence Sarah Hancock and their only son Henry James was born in 1914. Percy was a sharemilker near Palmerston North then moved to Springdale, near Waitoa, sharemilking for 40 years with the Walters family, as did Henry. They were well known and well respected in the Springdale district.
              </p>
            </div>

            {/* ARTHUR */}
            <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4">
              <p>
                <strong>ARTHUR (1881 – 1956)</strong> was a strong, independent, self-reliant child, who grew up on the pioneering bush covered block at Waiata. The Denton 2000 book records that at the age of nine, “when a schoolmaster threatened to thrash him for something he had done, Arthur ran away from school. He walked 11 miles to his father’s metalling camp. His father tried to get him to go home but he declared he was not going to let old Johnny Lowe belt him for nothing. He was told he would have to shovel metal if he stayed and he answered that it would be better than going to school.” He and his sister Anne leased the home farm and milked cows for five years until the family moved to Muhunoa East near Levin where he carried on milking cows in family partnerships until 1914. In 1916 he married a widow, Ada Sarah Cole, who had six daughters and two sons and they had two children – Shirley Ernest (born 1916) and Arnold Arthur (born 1917). Arthur bought land along the Otaki River at the end of Rahui Road and farmed there for the next 40 years.
              </p>

              {/* Image 49: Otaki River */}
              <div className="my-4">
                <ArchivalImageCard
                  src="/images/49 Otaki River.jpg"
                  alt="Otaki River"
                  caption="Otaki River, near Rahui Road farm"
                  filenameHint="49 Otaki River.jpg"
                />
              </div>
            </div>

            {/* ROY */}
            <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4">
              <p>
                <strong>ROY (1886 – 1971)</strong> worked with his father and brothers on the home farm, on their road works contracts in road and dairying near Levin, and then based with relatives in Gisborne. Then in 1911 he went pioneering in wild and rugged country at Rakauroa, between Gisborne and Opotoki, taking an occupation licence with the right of purchase of 511 acres. Roy married Letitia Kirkpatrick in 1917 and they had 10 children – Dulcie Jean, Letitia Laura, Annie Esther, Ernest Harold, Roy, Heather, Eric Taylor, Barrie (Bill), Frances Ena and Lynette June. After Roy’s brother Ernest was killed in WW1 Roy moved north of Auckland to farm at Whananaki, then to a farm at Poroti before retiring to the beach town of Ngunguru.
              </p>

              {/* Image 50: Ngunguru */}
              <div className="my-4">
                <ArchivalImageCard
                  src="/images/50 Ngunguru near Whangarei.jpg"
                  alt="Ngunguru near Whangarei"
                  caption="Ngunguru near Whangarei"
                  filenameHint="50 Ngunguru near Whangarei.jpg"
                />
              </div>
            </div>

            {/* OLIVE MAY */}
            <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-3">
              <p>
                <strong>OLIVE MAY (1891 – 1967)</strong> had the longest education of the family’s children. She stayed within the family unit until she was 18 and married Peter McFarland Cameron and they lived in Levin with their six children – Herbert Lloyd, Arthur Andrew, David William, John Edward, Anne Marjorie, James Ernest. The couple separated in the 1920’s and Anne went to Morrinsville. After their divorce Anne married widower and Mangateparu farmer Frederick Vickers in 1926 and they had two daughters, Rosella May and Violet Elizabeth. Frederick had one previous daughter, Mary Emily Scott. When she was widowed Anne was left with a farm to run which she did until 1950 when she went to live with her daughter Anne at Avondale.
              </p>
            </div>

            {/* GARRY EDWARD */}
            <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4">
              <p>
                <strong>GARRY EDWARD (1895 – 1949)</strong> was a toddler when his mother disappeared. He worked with family members and on contracts in and around Levin, marrying Clara Walter Tate in 1922. They had four children. Edward worked on farms, the at the Miranui Flaxmill, and moved back to Muhunoa East when the mill closed. His health deteriorated and he was confined to a wheelchair from about 1943.
              </p>

              {/* Images 51 & 52: Miranui Flaxmill & Drying flax */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4">
                <ArchivalImageCard
                  src="/images/51 Miranui Flaxmill opened 1907.jpg"
                  alt="Miranui Flaxmill opened 1907"
                  caption="Miranui Flaxmill opened 1907"
                  filenameHint="51 Miranui Flaxmill opened 1907.jpg"
                />
                <ArchivalImageCard
                  src="/images/52 Drying flax.jpg"
                  alt="Drying flax"
                  caption="Drying flax in the Manawatu"
                  filenameHint="52 Drying flax.jpg"
                />
              </div>
            </div>

          </div>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 5: Louisa Alice Denton */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              Louisa Alice Denton (1845 – 1942) – The Only Female Denton Pilgrim Settler
            </h2>
          </div>

          <p>
            Alice, as she was always known, lived her first years in the comfort and security of Rushbrooke Park Farm, Suffolk, and her last years in the raw spaces of Southland, where some of her descendants still live and farm. She was only five when she travelled out on Sir George Seymour in the comfort of a family cabin. She may well have remembered the sea and sunsets which were described by Cyrus Davie, who she would know well in another chapter of her life. “The sea and sunsets were very beautiful, water of the most beautiful blue, broken by crested waves and snowy whiteness. I never could have thought the mere atmospheric enjoyment could have been of so intense a kind: it is a kind of wonderfully dreamy sort of existence.”
          </p>

          <p>
            Comfort was not what lay ahead when they arrived in Canterbury, either at the crowded Pilgrim Spring encampment in Hagley Park or under canvas in Cathedral Square. Within nine months her mother and three siblings were dead. Her younger brother Thomas Charles was taken in by foster parents, but Alice had the good fortune to be later taken in by Charles Joseph and Fanny (Elizabeth Francis) Bridge, who married in England in 1854. On his first trip to NZ Charles had shared a cabin with Cyrus Davie on Randolph and their families remained friends for many years.
          </p>

          {/* Image 53: Louisa Alice Denton */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/53 Louisa Alice Denton.jpg"
              alt="Louisa Alice Denton"
              caption="Louisa Alice Denton (1845 – 1942)"
              filenameHint="53 Louisa Alice Denton.jpg"
            />
          </div>

          <p>
            The Bridges were from prosperous and educated families. Fanny’s father was a noted physician, Sir Charles Hastings. Charles was the son of Isabella (Fetcher) and James Oakes Bridge, a Liverpool merchant. Charles was a member of the Canterbury Association and travelled out first class as a Pilgrim. Alice would have met a wide range of influential settlers, church people, politicians, farmers, and professionals while living in the Bridge household, including Cyrus Davie who became the Chief Surveyor for the Canterbury Provincial Government.
          </p>

          <p>
            Charles farmed next to the Denton’s at Opawa, later buying about 750ha between Waihora Lake Ellesmere and the Rakaia River, Homebrook. He laid out a township, naming it after himself, Southbridge, and granting land for a school and 9 ha land for a church to be built. It was St James Anglican Church, consecrated 9 March 1863. Charles Bridge and his family lived at Homebrook from 1862 - 1876, when he died 10 days after being thrown from a buggy.
          </p>

          {/* Image 54: St James Southbridge */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/54 St James Southbridge.jpg"
              alt="St James Southbridge"
              caption="St James Anglican Church, Southbridge"
              filenameHint="54 St James Southbridge.jpg"
            />
          </div>

          <p>
            In his NZ diaries, held at the Christchurch Public Library, there are frequent mentions of Mr Denton, Arthur and Fred Denton. On a Sunday he walked with Mr Denton to the Park, and he did a lot of farming trade with the two younger Dentons.
          </p>

          <p>
            Alice was confirmed into the Church of England while she lived with the Bridges, in the first Confirmation Service undertaken by Bishop Harper. The Bridge family lived at Kaiapoi before they moved to the windy plains of Southbridge. It was there, as a 19-year-old, Alice was courted by Scotsman William Greig, who had arrived in 1860 on the William Miles. His employer references called him a first-class horseman and “he had proved himself to be a most excellent young man.” He was also a highly skilled ploughman, a sought-after expertise to develop the Canterbury plains.
          </p>

          <p>
            Alice and William married 26 April 1865 at Scotch Church, Kaiapoi. They had four children:
          </p>

          <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2.5">
            <ul className="list-disc list-outside pl-6 space-y-2 text-[#2c221e]">
              <li><strong>Augusta Georgiana (Gussie)</strong> (1866 – 1946) married butcher John Johnston at St Thomas’ in 1887 and they lived in the Hunterville and Palmerston North areas. They had three children.</li>
              <li><strong>Louisa Esther</strong> (1867 – 1900) married overseer Scots-born Robert Jackson at St Thomas’ in 1886, at one time manager of Molesworth and later Kawarau Station.</li>
              <li><strong>Arthur Alexander</strong> (1869 – 1952) got into a fight when he was a young man and knocked his opponent out so hard there were fears he might die, so Arthur, a tall well-built lad, took off and was out of contact for 20 years. Gussie finally found him in the Hawkes Bay-Gisborne area to tell him the man hadn’t died. Arthur had built a little grey house and lived in the Wairoa area.</li>
              <li><strong>William Frederick</strong> (1870 – 1944) moved south with his family, and later went into a farming partnership at Scotts Gap with his half-brother Herbert until about 1920. He went to the North Island where he cut flax and bush felling, returning four years later to buy a farm at Diamond Harbour. He married Emily Mary Small and they had one daughter, Lorna. They retired to Rangiora.</li>
            </ul>
          </div>

          <p>
            William died at Kaiapoi in 1870, aged only 35, leaving Alice with a baby so she had four children under five years of age. Alice’s younger brother Thomas came to live with them although he was often away on his agricultural contracting work. Alice looked after his extra horses while he was away with the work team.
          </p>

          {/* Image 55: Eyreton farm */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/55 Eyreton farm.jpg"
              alt="Eyreton farm"
              caption="Eyreton farm in North Canterbury"
              filenameHint="55 Eyreton farm.jpg"
            />
          </div>

          <p>
            Five years after William died Alice (30) married Edward Clothier (29) at St Thomas’ Church, East Eyreton, on 22 December 1875. Edward and his family had come out on Sir George Seymour, from Somerset and settled on Kaiapoi Island. Edward was farming in the Eyreton district, on Clothier Road, when he married.
          </p>

          {/* Image 56: 1884 Clothier farm */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/56 1884 Clothier farm.jpg"
              alt="1884 Clothier farm"
              caption="The Clothier farm in 1884 – Edward is on the far left and Herbert is on the horse."
              filenameHint="56 1884 Clothier farm.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              The Clothier farm in 1884 – Edward is on the far left and Herbert is on the horse.
            </p>
          </div>

          <p>
            Alice and Edward had five children while farming at Eyreton:
          </p>

          <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2.5">
            <ul className="list-disc list-outside pl-6 space-y-2 text-[#2c221e]">
              <li><strong>Herbert Edward</strong> (1877 – 1972) farmed with Will Greig for a start, then married Annie Margaret Dickson of Scotts Gap in 1914 and had two daughters, Sheila Leggett Denton and Elvie Esme Edith who died as an infant. They worked four years on a Clothier uncle’s farm at Hungahunga, returning to Southland and living with Annie’s father, John Dickson until they bought a farm at Strathvale, Otautau, called The Poplars, where they milked up to 12 cows by hand.</li>
              <li><strong>Eva Margaret</strong> (1878 – 1939) married Scots-born James Paterson in 1897 at her parent’s home at Otautau; they took up 150 ha at Scotts Gap, Dalmore. They had nine children and were strongly involved in their community. They operated the Scotts Gap post office and rang the local telephone exchange when it opened in 1902. They planted a large orchard. The farm remains in family ownership.</li>
              <li><strong>Ethel Robina</strong> (1879 – 1922) married Robert Salton, and had one daughter, Rona. Ethel and later Rona both died of consumption (TB), as had her father and his mother (Ann Clothier).</li>
              <li><strong>Archibald Harry</strong> (1882 – 1970) suffered from sciatica. He married Nell Hooper in 1907 and they had a dairy farm near Rotorua but sold that in 1914, moving to Cambridge where Archie worked at the Hautapu Dairy Factory for many years, driving milk tankers. They had two daughters, Evelyn Maud and Marjory Eileen. He had to walk off a farm at Rotorangi in the Depression. In WW2 he worked as a storeman at the Hopuhopu Military Camp.</li>
              <li><strong>Leonard Walter</strong> (1887 – 1904) died while working a team of horses. The Western Star reported it: “A young lad named Clothier of Scotts Gap in the employ of a Mt Linton settler, Mr Gordon, dropped dead on Wednesday October 26th while at work in a field with his team. A neighbouring settler passing by the paddock about 11 o’clock saw the team standing but thinking that the lad might be engaged fixing up some part of the plough, paid no attention to the matter. On passing by in the afternoon he saw the horses still in the same place and on going up to the spot found the lad laying dead by the side of his plough. Constable Dwan of Nightcaps was at once communicated with and he immediately went to the place. The lad had been suffering some time from heat disease with without doubt was the cause of death. It was not deemed necessary to hold an inquest.”</li>
            </ul>
          </div>

          <p>
            Canterbury had a wheat boom in the late 1870s and early 1880s but a drought, followed by a depression (resulting from unsustainable levels of public and private borrowings then hit by the collapse of the City Bank of Glasgow in 1878 which impacted on London, then the financial centre, and rippled out to the colonies) affected livelihoods. The Clothier’s neighbour, Edward Chilton, persuaded them to shift to Southland where they bought a farm at Otautau about 1894 – but Edward died of TB just three years later and Alice was again a widow, at 52 years of age. She stayed at Otautau, except for long visits to the North Island to stay with family members, and in 1940 she moved to Lorneville near Invercargill.
          </p>

          <p>
            When she was 95, Alice was subject of a local article. “In the early days settlers felt not only the loss of amenities of life to which they had been accustomed but the privations they were forced to undergo to get a living for the large families that they reared,” she said.
          </p>

          {/* Image 57: Takitimu Range */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/57 Takitimu Range by John K Reed.jpg"
              alt="Takitimu Range by John K Reed"
              caption="Takitimu Range by John K Reed"
              filenameHint="57 Takitimu Range by John K Reed.jpg"
            />
          </div>
        </section>

        <hr className="border-t-2 border-dashed border-[#d8ceb9] my-8" />

        {/* Section 6: The Clothier Connection */}
        <section className="space-y-6">
          <div className="border-b-2 border-[#d8ceb9] pb-3">
            <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              The Clothier Connection
            </h2>
          </div>

          <p>
            Edward Clothier’s parents, Samuel (1825 – 1887) and Ann Clothier (1824 – 1870), spent their first four years as bailiff/farm manager for William Guise Brittan, in Linwood. Clothier Street is named after this family – it runs between Tuam and Essex Streets on the east side of Fitzgerald Avenue. Brittan had paid the passages for the Clothier family. Samuel and Ann, from near Castle Cary in Somerset, had married in Hermitage Bay, Newfoundland, Canada, but they returned to England.
          </p>

          {/* Image 58: Castle Cary in Somerset */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/58 Castle Cary in Somerset.jpg"
              alt="Castle Cary in Somerset"
              caption="Castle Cary in Somerset"
              filenameHint="58 Castle Cary in Somerset.jpg"
            />
          </div>

          <p>
            Brittan, a surgeon, came out on Sir George Seymour as the ship’s doctor, and became Commissioner of Crown Lands. He had been one of the founders of the Canterbury Settlement and tried to enter political life but he wasn’t successful. Some reports claim he was an unpopular figure. His lasting good name is as one of the Founding Fathers of Cricket in Canterbury. His town estate was Linwood (meaning beautiful woods) and he also owned land at Halswell, Lansdowne, where the widely used Halswell stone was quarried.
          </p>

          <p>
            Sam Clothier was a trustee of the early Holy Trinity Church Avonside and he built the thick cob walls for 40 pounds.
          </p>

          <p>
            The Clothier’s bought their own land, calling it Cherry Farm, on the north side of the Waimakariri River, in the Eyreton area, on Clothier Road. He must have been a practical man, with good building skills. He is said to have built cob walls for several large buildings.
          </p>

          <p>
            Silverstream Reserve and walkway runs through part of the area where the Clothiers farmed, before flood protection and river channel realignments altered the course of the north branch of the Waimakariri River.
          </p>

          <p>
            Edward Clothier’s mother Mary Ann Clothier (nee Rose) was born and married in Canada. They moved to Samuel’s home at Castle Cary after their first son was born.
          </p>

          <p className="font-serif font-semibold text-[#4a3525]">
            Their children were:
          </p>

          <div className="bg-[#f3ede0]/80 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-2.5">
            <ul className="list-disc list-outside pl-6 space-y-1.5 text-[#2c221e]">
              <li><strong>Edward</strong> (born at Covey Bay, Newfoundland, Canada 1846 – 1897) married Louisa Alice Denton</li>
              <li><strong>William</strong> (1848-49, at Castle Cary)</li>
              <li><strong>James</strong> (1850 – 1935) married Margaret Gilles</li>
              <li><strong>William Henry</strong> (1852 – 1932)</li>
              <li><strong>Mary Jane</strong> (1854 – 1931)</li>
              <li><strong>George Charles</strong> (1856 – 1927) married Mary Alice Ashworth</li>
              <li><strong>Louisa Ellen</strong> (1859 – 1927) married Henry Wilson</li>
              <li><strong>Elizabeth Ann</strong> (1861 – 1893) married Augustus Vaughan</li>
              <li><strong>Jessie Albert</strong> (1863 – 1943) married Florence Elizabeth Edmonds</li>
              <li><strong>Frederick Obed</strong> (1866 – 1939) married Lucy Jane Martin</li>
            </ul>
          </div>

          <p>
            The family in the second generation did well in their chosen fields, mostly farming and several moved to the Hamilton area.
          </p>

          <p>
            It is interesting that the executors for Samuel’s will were his son Edward and Clarkville farmer, Launcelot Giles – two of his Giles granddaughters married Denton great nephews of Alice Clothier.
          </p>

          {/* Image 59: 1950 Clothier farm */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/59 1950 Clothier farm.jpg"
              alt="1950 Clothier farm"
              caption="On the hay, Sheila Brown (nee Clothier) and Jock Brown, 1950 (Photo: Denton 2000 book)"
              filenameHint="59 1950 Clothier farm.jpg"
            />
            <p className="font-serif italic text-sm md:text-base text-[#5c4328] text-center sm:text-left">
              On the hay, Sheila Brown (nee Clothier) and Jock Brown, 1950 (Photo: Denton 2000 book)
            </p>
          </div>
        </section>

      </article>

      {/* Part Navigation Footer */}
      {onSelectPart && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-[#f3ede0] rounded-3xl border-2 border-[#d8ceb9]">
          <button
            onClick={() => onSelectPart('part1')}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-serif text-sm font-bold border border-[#d8ceb9] transition"
          >
            <ArrowLeft className="w-4 h-4 text-[#7a5b3a]" />
            <span>Previous: Part 1 — Denton life in England pre-1850</span>
          </button>
          
          <button
            onClick={() => onSelectPart('part3')}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif text-sm font-bold transition shadow-xs"
          >
            <span>Next: Part 3 — Frederick Robert &amp; Esther Denton</span>
            <ArrowRight className="w-4 h-4 text-amber-200" />
          </button>
        </div>
      )}

    </div>
  );
};
